import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Platform,
  StyleSheet,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTranslation } from "../../i18n/useTranslation";

function toInputDateTimeLocal(isoString) {
  if (!isoString) return "";
  const dt = new Date(isoString);
  const tzOffsetMs = dt.getTimezoneOffset() * 60000;
  const localDt = new Date(dt.getTime() - tzOffsetMs);
  return localDt.toISOString().slice(0, 16);
}

export default function WizardStep1Details({
  isDarkMode,
  testDetails,
  onUpdateDetails,
  onNext,
  validationErrors = {},
}) {
  const c = getColors(isDarkMode);
  const { t } = useTranslation();

  const [localTitle, setLocalTitle] = useState(testDetails.title || "");
  const [localDesc, setLocalDesc] = useState(testDetails.description || "");
  const [localDuration, setLocalDuration] = useState(
    testDetails.duration_minutes != null ? String(testDetails.duration_minutes) : ""
  );
  const [localPassingScore, setLocalPassingScore] = useState(
    testDetails.passing_score != null ? String(testDetails.passing_score) : "60"
  );
  const [localMaxAttempts, setLocalMaxAttempts] = useState(
    testDetails.max_attempts != null ? String(testDetails.max_attempts) : "1"
  );

  useEffect(() => {
    setLocalTitle(testDetails.title || "");
    setLocalDesc(testDetails.description || "");
    setLocalDuration(
      testDetails.duration_minutes != null ? String(testDetails.duration_minutes) : ""
    );
    setLocalPassingScore(
      testDetails.passing_score != null ? String(testDetails.passing_score) : "60"
    );
    setLocalMaxAttempts(
      testDetails.max_attempts != null ? String(testDetails.max_attempts) : "1"
    );
  }, [testDetails]);

  const commitField = (field, value) => {
    onUpdateDetails({ ...testDetails, [field]: value });
  };

  const handleDateChange = (field, text) => {
    if (!text) {
      onUpdateDetails({ ...testDetails, [field]: "" });
      return;
    }
    const d = new Date(text);
    if (!isNaN(d.getTime())) {
      onUpdateDetails({ ...testDetails, [field]: d.toISOString() });
    }
  };

  return (
    <View>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.heading, { color: c.textPrimary }]}>{t("Test Details")}</Text>
        <Text style={[styles.subheading, { color: c.textSecondary }]}>
          {t("Set the basic information for your test. You can always come back to edit these later.")}
        </Text>
      </View>

      {/* Title */}
      <View style={styles.formGroup}>
        <Text style={[styles.label, { color: c.textSecondary }]}>{t("Test Title")}</Text>
        <TextInput
          style={[
            styles.input,
            {
              color: c.textPrimary,
              backgroundColor: c.inputBg,
              borderColor: validationErrors.title ? c.red : c.border,
            },
          ]}
          placeholder={t("Enter a descriptive test title...")}
          placeholderTextColor={c.textMuted}
          value={localTitle}
          onChangeText={setLocalTitle}
          onBlur={() => commitField("title", localTitle)}
        />
        {validationErrors.title && (
          <Text style={[styles.errorText, { color: c.red }]}>{validationErrors.title}</Text>
        )}
      </View>

      {/* Description */}
      <View style={styles.formGroup}>
        <Text style={[styles.label, { color: c.textSecondary }]}>{t("Description (optional)")}</Text>
        <TextInput
          style={[
            styles.input,
            styles.textarea,
            {
              color: c.textPrimary,
              backgroundColor: c.inputBg,
              borderColor: c.border,
            },
          ]}
          placeholder={t("Brief description of what this test covers...")}
          placeholderTextColor={c.textMuted}
          value={localDesc}
          onChangeText={setLocalDesc}
          onBlur={() => commitField("description", localDesc)}
          multiline
          numberOfLines={3}
        />
      </View>

      {/* Date row */}
      <View style={styles.formRow}>
        <View style={styles.formGroupHalf}>
          <Text style={[styles.label, { color: c.textSecondary }]}>{t("Start Date & Time")}</Text>
          {Platform.OS === "web" ? (
            <input
              type="datetime-local"
              value={toInputDateTimeLocal(testDetails.start_time)}
              onChange={(e) => handleDateChange("start_time", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: `1px solid ${validationErrors.schedule ? c.red : c.border}`,
                borderRadius: 6,
                fontSize: 14,
                color: c.textPrimary,
                backgroundColor: c.inputBg,
                fontFamily: "inherit",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          ) : (
            <TextInput
              style={[
                styles.input,
                {
                  color: c.textPrimary,
                  backgroundColor: c.inputBg,
                  borderColor: validationErrors.schedule ? c.red : c.border,
                },
              ]}
              placeholder="YYYY-MM-DDTHH:MM"
              placeholderTextColor={c.textMuted}
              value={toInputDateTimeLocal(testDetails.start_time)}
              onChangeText={(t) => handleDateChange("start_time", t)}
            />
          )}
        </View>
        <View style={styles.formGroupHalf}>
          <Text style={[styles.label, { color: c.textSecondary }]}>{t("End Date & Time")}</Text>
          {Platform.OS === "web" ? (
            <input
              type="datetime-local"
              value={toInputDateTimeLocal(testDetails.end_time)}
              onChange={(e) => handleDateChange("end_time", e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                border: `1px solid ${validationErrors.schedule ? c.red : c.border}`,
                borderRadius: 6,
                fontSize: 14,
                color: c.textPrimary,
                backgroundColor: c.inputBg,
                fontFamily: "inherit",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          ) : (
            <TextInput
              style={[
                styles.input,
                {
                  color: c.textPrimary,
                  backgroundColor: c.inputBg,
                  borderColor: validationErrors.schedule ? c.red : c.border,
                },
              ]}
              placeholder="YYYY-MM-DDTHH:MM"
              placeholderTextColor={c.textMuted}
              value={toInputDateTimeLocal(testDetails.end_time)}
              onChangeText={(t) => handleDateChange("end_time", t)}
            />
          )}
        </View>
      </View>
      {validationErrors.schedule && (
        <Text style={[styles.errorText, { color: c.red, marginTop: -12, marginBottom: 12 }]}>
          {validationErrors.schedule}
        </Text>
      )}

      {/* Duration / Passing / Attempts row */}
      <View style={styles.formRow3}>
        <View style={styles.formGroupThird}>
          <Text style={[styles.label, { color: c.textSecondary }]}>{t("Duration (minutes)")}</Text>
          <TextInput
            style={[
              styles.input,
              {
                color: c.textPrimary,
                backgroundColor: c.inputBg,
                borderColor: validationErrors.duration ? c.red : c.border,
              },
            ]}
            placeholder="45"
            placeholderTextColor={c.textMuted}
            value={localDuration}
            onChangeText={setLocalDuration}
            onBlur={() => commitField("duration_minutes", localDuration)}
            keyboardType="numeric"
          />
          {validationErrors.duration && (
            <Text style={[styles.errorText, { color: c.red }]}>{validationErrors.duration}</Text>
          )}
        </View>
        <View style={styles.formGroupThird}>
          <Text style={[styles.label, { color: c.textSecondary }]}>{t("Passing Score (%)")}</Text>
          <TextInput
            style={[
              styles.input,
              {
                color: c.textPrimary,
                backgroundColor: c.inputBg,
                borderColor: c.border,
              },
            ]}
            placeholder="60"
            placeholderTextColor={c.textMuted}
            value={localPassingScore}
            onChangeText={setLocalPassingScore}
            onBlur={() => commitField("passing_score", localPassingScore)}
            keyboardType="numeric"
          />
        </View>
        <View style={styles.formGroupThird}>
          <Text style={[styles.label, { color: c.textSecondary }]}>{t("Max Attempts")}</Text>
          <TextInput
            style={[
              styles.input,
              {
                color: c.textPrimary,
                backgroundColor: c.inputBg,
                borderColor: c.border,
              },
            ]}
            placeholder="1"
            placeholderTextColor={c.textMuted}
            value={localMaxAttempts}
            onChangeText={setLocalMaxAttempts}
            onBlur={() => commitField("max_attempts", localMaxAttempts)}
            keyboardType="numeric"
          />
        </View>
      </View>

      {/* Actions */}
      <View style={[styles.stepActions, { borderTopColor: c.border }]}>
        <View />
        <TouchableOpacity
          style={[styles.btnPrimary, { backgroundColor: c.accent }]}
          onPress={onNext}
          activeOpacity={0.7}
        >
          <Text style={styles.btnPrimaryText}>{t("Continue to Questions")}</Text>
          <MaterialCommunityIcons name="chevron-right" size={16} color="#fff" />
        </TouchableOpacity>
      </View>
    </View>
  );
}

function getColors(isDarkMode) {
  if (isDarkMode) {
    return {
      textPrimary: "#f1f5f9",
      textSecondary: "#94a3b8",
      textMuted: "#64748b",
      border: "#334155",
      inputBg: "#1e293b",
      accent: "#818cf8",
      red: "#f87171",
    };
  }
  return {
    textPrimary: "#0f172a",
    textSecondary: "#475569",
    textMuted: "#94a3b8",
    border: "#e2e8f0",
    inputBg: "#f8fafc",
    accent: "#6366f1",
    red: "#dc2626",
  };
}

const styles = StyleSheet.create({
  header: { marginBottom: 24 },
  heading: { fontSize: 24, fontWeight: "400", marginBottom: 6 },
  subheading: { fontSize: 14, lineHeight: 21 },
  formGroup: { marginBottom: 20 },
  label: {
    fontSize: 13,
    fontWeight: "600",
    marginBottom: 6,
    letterSpacing: 0.2,
  },
  input: {
    width: "100%",
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderRadius: 6,
    fontSize: 14,
  },
  textarea: {
    minHeight: 80,
    textAlignVertical: "top",
  },
  formRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 20,
  },
  formGroupHalf: {
    flex: 1,
  },
  formRow3: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 20,
  },
  formGroupThird: {
    flex: 1,
  },
  stepActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
  },
  btnPrimary: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 10,
    shadowColor: "rgba(99,102,241,0.25)",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  btnPrimaryText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
  errorText: {
    fontSize: 12,
    marginTop: 4,
  },
});
